import type { CSSProperties } from 'react';
import { Box, Button, Group, Modal, NumberInput, Stack, Text } from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';

import type { DraftSummary, DraftValue } from '@/utils/useItemsDraft';

import { MutationButton } from './MutationButton';

/** Pozadina retka s nespremljenom promjenom (izmjena, nova, za uklanjanje, greška). */
export const DRAFT_ROW_BACKGROUND = 'var(--color-row-draft)';

interface DraftNumberInputProps {
  value: DraftValue;
  /** Spremljena vrijednost (kod nove stavke: početna vrijednost) — polje je izmijenjeno kad se razlikuje. */
  savedValue?: number;
  /** Nova stavka: izmjena se označava rubom, ali bez "stara vrijednost X" (stare vrijednosti nema). */
  isNew?: boolean;
  error?: string | null;
  disabled?: boolean;
  width: number;
  /** 32 na tablici (web), 40 na kartici (mobitel). */
  height?: number;
  allowDecimal?: boolean;
  decimalScale?: number;
  formatSaved?: (value: number) => string;
  ariaLabel?: string;
  onChange: (value: DraftValue) => void;
}

/**
 * Brojčano polje stavke u nacrtu: izmijenjeno = zlatni rub + "stara vrijednost X" ispod, greška =
 * crveni rub + poruka ispod. Ništa ne sprema samo — vrijednost ide u nacrt, sprema se gumbom
 * "Spremi stavke".
 */
export const DraftNumberInput = ({
  value,
  savedValue,
  isNew,
  error,
  disabled,
  width,
  height = 32,
  allowDecimal = false,
  decimalScale,
  formatSaved = String,
  ariaLabel,
  onChange
}: DraftNumberInputProps) => {
  const { t } = useTranslation();
  const isDirty = !disabled && savedValue != null && value !== savedValue;
  const borderColor = error
    ? 'var(--color-status-danger)'
    : isDirty
      ? 'var(--color-accent-primary)'
      : 'var(--color-border-default)';

  const inputStyle: CSSProperties = {
    width,
    height,
    borderRadius: 6,
    border: `${error || isDirty ? 1.5 : 1}px solid ${borderColor}`,
    backgroundColor: error || isDirty ? 'var(--color-bg-surface)' : 'var(--color-bg-surface-2)',
    color: 'var(--color-text-primary)',
    fontSize: height > 32 ? 14 : 13,
    fontWeight: 600,
    padding: '0 10px',
    textAlign: 'right'
  };

  return (
    <Stack gap={2} align="flex-end">
      <NumberInput
        hideControls
        allowNegative={false}
        allowDecimal={allowDecimal}
        decimalScale={decimalScale}
        disabled={disabled}
        value={value}
        aria-label={ariaLabel}
        onChange={(next) => onChange(next === '' ? '' : Number(next))}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
        classNames={{ input: 'no-zoom-input' }}
        styles={{ input: inputStyle }}
      />
      {error ? (
        <Text fz={11.5} fw={600} ta="right" style={{ color: 'var(--color-status-danger)' }}>
          {error}
        </Text>
      ) : (
        isDirty &&
        !isNew && (
          <Text
            fz={11}
            ta="right"
            style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}
          >
            {t('itemsDraft.wasValue', { value: formatSaved(savedValue) })}
          </Text>
        )
      )}
    </Stack>
  );
};

export const NewItemBadge = () => {
  const { t } = useTranslation();
  return (
    <Text
      span
      fz={10.5}
      fw={600}
      style={{
        color: 'var(--color-text-primary)',
        backgroundColor: 'var(--color-badge-new)',
        borderRadius: 4,
        padding: '2px 6px',
        whiteSpace: 'nowrap',
        flexShrink: 0
      }}
    >
      {t('itemsDraft.newBadge')}
    </Text>
  );
};

/** Gumb "↺ Vrati" u stupcu Ukloni za stavku označenu za uklanjanje. */
export const UndoRemoveButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <Text
      fz={12.5}
      fw={600}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick();
      }}
      style={{
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        color: 'var(--color-accent-primary-active)'
      }}
    >
      ↺ {t('itemsDraft.undo')}
    </Text>
  );
};

const Dot = ({ color }: { color: string }) => (
  <Box
    style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }}
  />
);

/**
 * Stanje spremanja iznad tablice stavki: "Nespremljene promjene" dok postoje promjene, a
 * "Sve promjene spremljene · 14:05" samo odmah nakon spremanja — nestaje čim korisnik ponovno
 * krene mijenjati stavke. Bez promjena i bez spremanja ne prikazuje ništa. Greška se pokazuje
 * samo u polju i traci (Figma).
 */
export const ItemsSaveStatus = ({
  summary,
  justSaved,
  savedAt
}: {
  summary: DraftSummary;
  /** Stavke su upravo spremljene i od tada se ništa nije mijenjalo. */
  justSaved: boolean;
  /** Vrijeme tog spremanja (npr. "14:05"). */
  savedAt?: string | null;
}) => {
  const { t } = useTranslation();
  if (summary.total > 0) {
    return (
      <Group gap={6} wrap="nowrap">
        <Dot color="var(--color-accent-primary)" />
        <Text fz={12.5} fw={500} style={{ color: 'var(--color-text-secondary)' }}>
          {t('itemsDraft.statusDirty')}
        </Text>
      </Group>
    );
  }
  if (!justSaved) return null;
  return (
    <Text fz={12.5} fw={600} style={{ color: 'var(--color-status-success)' }}>
      ✓ {t('itemsDraft.statusSaved')}
      {savedAt ? ` · ${savedAt}` : ''}
    </Text>
  );
};

/**
 * Traka za spremanje: broj promjena, "Odustani" i "Spremi stavke". Na webu ispod tablice; na
 * mobitelu zalijepljena na dno ekrana (gumbi pola-pola), uz razmak da ne prekrije sadržaj.
 */
export const ItemsSaveBar = ({
  summary,
  isSaving,
  onCancel,
  onSave,
  variant = 'inline',
  scope,
  saveLabel
}: {
  summary: DraftSummary;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
  variant?: 'inline' | 'mobile';
  /** Na što se promjene odnose (npr. prostor u razradi) — "… u prostoru X". */
  scope?: string;
  /** Tekst gumba za spremanje, ako nije "Spremi stavke" (npr. "Spremi sate"). */
  saveLabel?: string;
}) => {
  const { t } = useTranslation();
  if (summary.total === 0) return null;

  const parts = [
    summary.changed > 0 && t('itemsDraft.changed', { count: summary.changed }),
    summary.added > 0 && t('itemsDraft.added', { count: summary.added }),
    summary.removed > 0 && t('itemsDraft.removed', { count: summary.removed })
  ].filter(Boolean);
  const hasErrors = summary.errors > 0;
  const dot = (
    <Dot color={hasErrors ? 'var(--color-status-danger)' : 'var(--color-accent-primary)'} />
  );
  const title = (
    <Text fz={13.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
      {scope
        ? t('itemsDraft.unsavedCountIn', { count: summary.total, scope })
        : t('itemsDraft.unsavedCount', { count: summary.total })}
    </Text>
  );
  const detail = hasErrors
    ? t('itemsDraft.errorsSummary', { count: summary.errors })
    : parts.join(' · ');
  const detailColor = hasErrors ? 'var(--color-status-danger)' : 'var(--color-text-secondary)';

  if (variant === 'mobile') {
    return (
      <>
        {/* Razmak ispod sadržaja — fiksna traka inače prekrije zadnju karticu i ukupno. */}
        <Box h={140} />
        <Stack
          role="region"
          aria-label={t('itemsDraft.saveItems')}
          gap={10}
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            padding: '14px 16px calc(14px + env(safe-area-inset-bottom, 0px))',
            backgroundColor: 'var(--color-bg-surface-2)',
            borderTop: '1px solid var(--color-border-soft)'
          }}
        >
          <Stack gap={4}>
            <Group gap={8} wrap="nowrap">
              {dot}
              {title}
            </Group>
            <Text fz={12.5} style={{ color: detailColor }}>
              {detail}
            </Text>
          </Stack>
          <Group gap={8} grow wrap="nowrap">
            <Button variant="outline" onClick={onCancel} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <MutationButton isPending={isSaving} disabled={hasErrors} onClick={onSave}>
              {saveLabel ?? t('itemsDraft.saveItems')}
            </MutationButton>
          </Group>
        </Stack>
      </>
    );
  }

  return (
    <Group
      role="region"
      aria-label={t('itemsDraft.saveItems')}
      gap={12}
      wrap="wrap"
      style={{
        padding: '12px 20px',
        backgroundColor: 'var(--color-bg-surface-2)',
        borderTop: '1px solid var(--color-border-soft)',
        position: 'sticky',
        bottom: 0,
        zIndex: 5
      }}
    >
      <Group gap={8} wrap="wrap" style={{ flex: 1, minWidth: 220 }}>
        {dot}
        {title}
        <Text fz={13} style={{ color: detailColor }}>
          · {detail}
        </Text>
      </Group>
      <Button size="xs" variant="outline" onClick={onCancel} disabled={isSaving}>
        {t('common.cancel')}
      </Button>
      <MutationButton size="xs" isPending={isSaving} disabled={hasErrors} onClick={onSave}>
        {saveLabel ?? t('itemsDraft.saveItems')}
      </MutationButton>
    </Group>
  );
};

interface UnsavedChangesGuardOptions {
  summary: DraftSummary;
  /** Spremi nacrt; true ako je spremanje uspjelo (tek tada se nastavlja). */
  onSave: () => Promise<boolean>;
  onDiscard: () => void;
}

/**
 * Pitanje "Odbaci / Ostani / Spremi i izađi" kad korisnik napušta ekran s nespremljenim
 * stavkama — navigacija unutar aplikacije (useBlocker), zatvaranje/osvježavanje kartice
 * (beforeunload) i akcije na istom ekranu koje traže spremljene stavke (guard).
 */
export const useUnsavedChangesGuard = ({
  summary,
  onSave,
  onDiscard
}: UnsavedChangesGuardOptions) => {
  const { t } = useTranslation();
  const isDirty = summary.total > 0;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  );
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  /** Akcija koja traži spremljene stavke — s nespremljenima prvo pita. */
  const guard = useCallback(
    (action: () => void) => {
      if (isDirty) setPendingAction(() => action);
      else action();
    },
    [isDirty]
  );

  const opened = blocker.state === 'blocked' || pendingAction !== null;

  const proceed = () => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    } else {
      const action = pendingAction;
      setPendingAction(null);
      action?.();
    }
  };

  const stay = () => {
    if (blocker.state === 'blocked') blocker.reset();
    setPendingAction(null);
  };

  const discard = () => {
    onDiscard();
    proceed();
  };

  const saveAndLeave = async () => {
    setIsSaving(true);
    try {
      if (await onSave()) proceed();
      else stay();
    } finally {
      setIsSaving(false);
    }
  };

  const modal = (
    <Modal
      opened={opened}
      onClose={stay}
      centered
      size={520}
      radius="lg"
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.55 }}
      styles={{
        content: {
          backgroundColor: 'var(--color-bg-surface)',
          boxShadow: '0px 16px 20px rgba(0, 0, 0, 0.15)'
        },
        body: { padding: 0 }
      }}
    >
      <Stack gap={16} p={{ base: 20, sm: 24 }}>
        <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('itemsDraft.guardTitle')}
        </Text>
        <Text fz={13} style={{ lineHeight: '20px', color: 'var(--color-text-secondary)' }}>
          {t('itemsDraft.guardMessage', { count: summary.total })}
          {summary.errors > 0 && ` ${t('itemsDraft.guardErrors')}`}
        </Text>
        <Group gap={8} wrap="wrap">
          <Button variant="outline" color="red" onClick={discard} disabled={isSaving}>
            {t('itemsDraft.guardDiscard')}
          </Button>
          <Box style={{ flex: 1 }} />
          <Button variant="outline" onClick={stay} disabled={isSaving} data-autofocus>
            {t('itemsDraft.guardStay')}
          </Button>
          <MutationButton isPending={isSaving} disabled={summary.errors > 0} onClick={saveAndLeave}>
            {t(
              blocker.state === 'blocked'
                ? 'itemsDraft.guardSaveAndLeave'
                : 'itemsDraft.guardSaveAndContinue'
            )}
          </MutationButton>
        </Group>
      </Stack>
    </Modal>
  );

  return { guard, modal };
};
